/* global $ */ // Declare $ as a global variable for jQuery usage
import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import { get_token, getCookie } from '@/router/index.js';
import Swal from 'sweetalert2';

export default {
  name: 'RoleView',
  components: { Sidebar, Navbar },

  data: () => ({
    csrfToken: '',
    dataTable: null,

    // Lists
    roles: [],
    roles_loading: false,
    available_permissions: [],
    role_permissions: [],
    selected_role_details: null,

    // UI
    filter_type: 'all',
    role_loading: false,
    permission_search: '',

    // Add modal
    role_name: '',
    role_description: '',

    // Update/Manage modal
    id: null,
    name: '',
    description: '',
    selected_permission_ids: [],

    should_reload_after_modal: false, 
  }),

  computed: {
    filtered_roles() {
      if (this.filter_type === 'active') return this.roles.filter(r => r.is_active);
      if (this.filter_type === 'inactive') return this.roles.filter(r => !r.is_active);
      return this.roles;
    },
    active_roles_count() {
      return this.roles.filter(r => r.is_active).length;
    },
    inactive_roles_count() {
      return this.roles.filter(r => !r.is_active).length;
    },
    filtered_available_permissions() {
      if (!this.permission_search) return this.available_permissions;
      const search = this.permission_search.toLowerCase();
      return this.available_permissions.filter(p =>
        (p.name || '').toLowerCase().includes(search) ||
        (p.description || '').toLowerCase().includes(search)
      );
    }
  },

  mounted() {
    this.csrfToken = getCookie('csrftoken');
    this.load_role_data();
    this.attach_modal_listener();
  },

  methods: {
    // ---------------- helpers ----------------
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },

    async open_manage_permissions_modal(role) {
      this.$router.push({
            name: 'RolePermissions',
            params: { id: role.id }
        });

    },

    attach_modal_listener() {
      // Reset add/update forms on close (optional but tidy)
      this.$nextTick(() => {
        const $add = $('#addRoleModal');
        const $upd = $('#updateRoleModal');

        $add.off('hidden.bs.modal').on('hidden.bs.modal', () => {
          this.role_name = '';
          this.role_description = '';
          this.role_loading = false;

          document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
          document.body.classList.remove('modal-open');
          document.body.style.removeProperty('overflow');
          document.body.style.removeProperty('padding-right');

          if (this.should_reload_after_modal) {
            this.should_reload_after_modal = false;
            this.load_role_data();
          }
        });
        $upd.off('hidden.bs.modal').on('hidden.bs.modal', () => {
          this.id = null;
          this.name = '';
          this.description = '';
          this.role_loading = false;

          document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
          document.body.classList.remove('modal-open');
          document.body.style.removeProperty('overflow');
          document.body.style.removeProperty('padding-right');

          if (this.should_reload_after_modal) {
            this.should_reload_after_modal = false;
            this.load_role_data();
          }
        });
      });
    },

    destroy_roles_table() {
      const $table = $('#roles_table');
      if ($.fn.DataTable && $.fn.DataTable.isDataTable($table)) {
        $table.DataTable().clear().destroy();
      }
      this.dataTable = null;
    },

    build_roles_table() {
      // If your table rows come from v-for (they do), just init DT after DOM updates
      this.$nextTick(() => {
        const $table = $('#roles_table');
        if (!$table.length || !$.fn.DataTable) return;
        this.destroy_roles_table();
        this.dataTable = $table.DataTable({
          pageLength: 10,
          lengthChange: false,
          searching: true,
          ordering: true,
          paging: true,
          info: true,
          autoWidth: false
        });
      });
    },

    normalize_role_data: (rd = {}) => ({
      id: rd.id ?? null,
      name: rd.name ?? '',
      description: rd.description ?? '',
      is_active: rd.is_active ?? true,
      permissions_count: rd.permissions_count ?? 0,
      created_by: rd.created_by_name ?? '',
    }),

    // ---------------- load wrapper ----------------
    async load_role_data() {
      this.roles_loading = true;
      try {
        await this.get_roles();
      } finally {
        this.roles_loading = false;
        this.build_roles_table();
      }
    },

    // ---------------- API calls ----------------
    get_roles() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();

      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_permission_roles/`,
        method: 'GET',
        xhrFields: { withCredentials: true },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': this.csrfToken,
          'X-Page-URL': window.location.href
        }
      }).then((data) => {
        if (data?.status === 'success') {
          const role_list = data.data || [];
          this.roles = role_list.map(r => this.normalize_role_data(r));
        } else {
          this.$notyf.info(data?.message || 'Failed to fetch roles');
          this.roles = [];
        }
      }).catch((jq) => {
        this.$notyf.error(jq?.responseJSON.message || `Error fetching roles: HTTP ${jq.status}`);
        this.roles = [];
      });
    },

    // ---------------- Modal handlers ----------------
    open_update_modal(role) {
      this.id = role.id;
      this.name = role.name || '';
      this.description = role.description || '';
    },

    // ---------------- Create ----------------
    add_role() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      const name = (this.role_name || '').trim();
      if (!name) {
        this.$notyf.info('Please enter a role name');
        return;
      }

      this.role_loading = true;

      $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_role/`,
        method: 'POST',
        data: JSON.stringify({
          name,
          description: this.role_description || '',
        }),
        contentType: 'application/json',
        xhrFields: { withCredentials: true },
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': this.csrfToken,
          'X-Page-URL': window.location.href
        }
      }).then((data) => {
        if (data?.status === 'success') {
          this.$notyf.success('Role created successfully.');
          this.role_name = '';
          this.role_description = '';
          this.should_reload_after_modal = true;
          const modalEl = document.getElementById('addRoleModal');
          window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
        } else {
          this.$notyf.error(data?.message || 'Failed to create role');
        }
      }).catch((jq) => {
        this.$notyf.error( jq?.responseJSON.message);
      }).always(() => {
        this.role_loading = false;
      });
    },

    // ---------------- Update ----------------
    update_role_basic_info() {
      if (!this.id) return;

      const token = this.require_token_or_redirect();
      if (!token) return;

      this.role_loading = true;

      $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/update_role/`,
        method: 'POST',
        data: JSON.stringify({
          role_id: this.id,
          name: this.name || '',
          description: this.description || ''
        }),
        contentType: 'application/json',
        xhrFields: { withCredentials: true },
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': this.csrfToken,
          'X-Page-URL': window.location.href
        }
      }).then((data) => {
        if (data?.status === 'success') {
          this.$notyf.success('Role updated successfully.');
          this.should_reload_after_modal = true;
          const modalEl = document.getElementById('updateRoleModal');
          window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
        } else {
          this.$notyf.error(data?.message || 'Failed to update role');
        }
      }).catch((jq) => {
        this.$notyf.error(`Error updating role: HTTP ${jq.status}`);
      }).always(() => {
        this.role_loading = false;
      });
    },

    // ---------------- Status toggle ----------------
    toggle_role_status(role) {
      const will_disable = !!role.is_active;
      const title = will_disable ? 'Disable Role?' : 'Enable Role?';
      const verb = will_disable ? 'disable' : 'enable';

      Swal.fire({
        title,
        text: `Are you sure you want to ${verb} "${role.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Yes, ${verb} it!`,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (!result.isConfirmed) return;

        const token = this.require_token_or_redirect();
        if (!token) return;

        const path = will_disable
          ? '/aic_one_api/system_management/deactivate_role/'
          : '/aic_one_api/system_management/activate_role/';

        $.ajax({
          url: `${process.env.VUE_APP_API_BASE_URL}${path}`,
          method: 'POST',
          data: JSON.stringify({ role_id: role.id }),
          contentType: 'application/json',
          xhrFields: { withCredentials: true },
          headers: {
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        }).then((data) => {
          if (data?.status === 'success') {
            this.$notyf.success('Role status updated successfully!');
            return this.load_role_data();
          } else {
            this.$notyf.error(data?.message || 'Failed to update status');
          }
        }).catch((jq) => {
          this.$notyf.error(`Error updating status: HTTP ${jq.status}`);
        });
      });
    },
    truncate_text(text, length = 50) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
  }
};
